import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Utils } from 'app/components/utils/Utils';
import { Log } from 'app/core/logging/log';
import { ParameterCart } from 'app/models/cart-model';
import { StockModel } from 'app/models/stock-model';
import { AppService } from 'app/services/app/app.service';
import { SharedModule } from 'app/shared.module';
import * as moment from 'moment-timezone';
import { Subscription } from 'rxjs';



type ModeSelect = 'datehour' | 'onlydate' | 'datebutton';
interface TimeStruct { hour: number; minute: number; second?: number; }


@Component({
    selector: 'app-date-time-range-picker',
    templateUrl: './date-time-range-picker.component.html',
    standalone: true,
    imports: [SharedModule],
    styleUrls: ['./date-time-range-picker.component.scss']
})
export class DateTimeRangePickerComponent implements OnInit, OnDestroy {

    parameters: ParameterCart;
    fromDate: moment.Moment;
    toDate: moment.Moment;

    @Input() showDetail: boolean = false;
    @Input() minPrice: number = 0;
    @Output() formReady = new EventEmitter<any>();
    @Output() formUpdated = new EventEmitter<any>();

    DIFF_DAYS_DEFAUL = 0;
    MAX_DAYS_FUTURE = 90;
    modeSelect: ModeSelect = 'datehour';

    fromTimeOptions: string[] = [];
    toTimeOptions: string[] = [];
    currentStock$: Subscription;
    openingHours: string;
    closingHours: string;
    openingHoursUtc: string;
    closingHoursUtc: string;
    maxHours: number;
    tz: string;

    TIME_ZONE = 'Europe/Madrid';
    form!: FormGroup;
    minDate: moment.Moment;
    maxDate: moment.Moment;


    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private appService: AppService
    ) {
        moment.locale('es');
        this.parameters = Utils.getParams(this.route.snapshot.queryParamMap);
    }

    ngOnInit(): void {

        const now = this.getNow();
        const to = this.getNow();

        this.form = this.fb.group({
            fromDate: [{ value: now.toDate(), disabled: this.modeSelect === 'datebutton' }, Validators.required],
            toDate: [{ value: to.toDate(), disabled: this.modeSelect === 'datebutton' }, Validators.required],
            fromTime: ['', Validators.required],
            toTime: ['', Validators.required]
        });
        this.form.get('toDate')?.valueChanges.subscribe((value) => this.onDateSelection());
        this.form.get('fromTime')?.valueChanges.subscribe((value) => this.onChangeFromTime(value));
        this.form.get('toTime')?.valueChanges.subscribe((value) => this.onChangeToTime(value));
        this.formReady.emit(this.form);

        this.fromDate = now;
        this.toDate = to;

        this.currentStock$ = this.appService.currentStock$.subscribe((stock: StockModel) => {
            this.tz = (stock as any).tz || this.TIME_ZONE;
            this.openingHoursUtc = stock.openingHours;
            this.closingHoursUtc = stock.closingHours;
            this.maxHours = stock.items.find(f => f.id === this.parameters.type)?.maxHours?.['TIME_HOUR'] ?? 23;

            let stockNow = this.getNow();
            let stockTo = this.getNow();
            let future = this.getNow().add(this.MAX_DAYS_FUTURE, 'd');

            if (this.modeSelect === 'datebutton') {
                stockTo.add(1, 'd');
            } else {
                const today = stockNow.clone().startOf('day');
                const todayClosing = this.getLocalTimeFromUtcHours(today, this.closingHoursUtc);
                const lastStartToday = todayClosing.clone().subtract(1, 'hours');

                this.openingHours = this.getLocalTimeFromUtcHours(today, this.openingHoursUtc).format('HH:mm');
                this.closingHours = todayClosing.format('HH:mm');

                if (stockNow.isAfter(lastStartToday)) {
                    stockNow.add(1, 'd');
                    stockTo.add(1, 'd');
                    future.add(1, 'd');
                }
            }

            this.minDate = stockNow.clone().startOf('day');
            this.maxDate = future.clone().startOf('day');
            this.fromDate = stockNow.clone();
            this.toDate = stockTo.clone();
            this.form.patchValue(
                {
                    fromDate: this.fromDate.toDate(),
                    toDate: this.toDate.toDate()
                },
                { emitEvent: false }
            );

            if (this.modeSelect == 'datebutton') {
                const fromTime = this.mapMomentAsTimeStruct(this.fromDate);
                const toTime = this.mapMomentAsTimeStruct(this.toDate);
                this.form.patchValue(
                    {
                        fromTime: this.formatTimeStruct(fromTime),
                        toTime: this.formatTimeStruct(toTime)
                    },
                    { emitEvent: false }
                );
            } else {
                this.configureTimeOptions();
                this.toUpdateTimeFields();
            }


            //   this.updateCart();

            setTimeout(() => this.updateCart(), 100);
        });



    }

    ngOnDestroy(): void {
        this.currentStock$.unsubscribe();

    }

    get fromTime() {
        const field = this.form.get('fromTime');
        if (field) {
            return this.mapStringTimeToTimeStruct(field?.value);
        } else {
            return null;
        }
    }

    get toTime() {
        const field = this.form.get('toTime');
        if (field) {
            return this.mapStringTimeToTimeStruct(field?.value);
        } else {
            return null;
        }
    }

    // ========================================================
    // =========== RECALCULOS AUTOMÁTICOS =====================
    // ========================================================

    /**
     * un dia:
        - today: 
            - rango de inicio: horaActual...horaCierreMenosInterval
            - rango de finalizacion: horaInicioMasPeriod...horaCierre
    
        - notoday:
            - rango de inicio: horaApertura...horaCierreMenosInterval
            - rango de finalizacion: horaInicioMasPeriod...horaCierre

    * varios día:
        - rango de inicio: horaApertura...horaCierreMenosInterval
        - rango de finalización: horaInicioMasPeriod...horaCierre

     * @param reset 
     */
    configureTimeOptions() {
        const minPeriod = 1;
        const interval = 60;

        const now = this.getNow();
        let startStartRange = this.getLocalTimeFromUtcHours(this.fromDate, this.openingHoursUtc);
        let endStartRange = this.getLocalTimeFromUtcHours(this.fromDate, this.closingHoursUtc);
        let startEndRange = this.getLocalTimeFromUtcHours(this.toDate || this.fromDate, this.openingHoursUtc);
        let endEndRange = this.getLocalTimeFromUtcHours(this.toDate || this.fromDate, this.closingHoursUtc);

        this.openingHours = startStartRange.format('HH:mm');
        this.closingHours = endStartRange.format('HH:mm');

        //--------------- Start Range ------------------//
        const isOneDay = this.fromDate.isSame(this.toDate, 'date');
        const isToday = this.fromDate.isSame(now, 'date');
        if (isToday && now.isAfter(startStartRange)) {
            startStartRange.set('hours', now.hours());
            startStartRange.set('minutes', now.minutes());
        }

        if (isOneDay) {
            endStartRange.subtract(minPeriod, 'hours');
        } else {
            endStartRange.subtract(interval, 'minutes');
        }


        //--------------- End Range ------------------//

        if (isOneDay) {
            startEndRange = startStartRange.clone();
            if (this.fromTime != null) {
                startEndRange.set('hours', this.fromTime.hour);
                startEndRange.set('minutes', this.fromTime.minute);
            }
            startEndRange.add(minPeriod, 'hours');
        } else {
            //  startEndRange.add(interval, 'minutes');
        }

        Log.debug('isOneDay', {isOneDay});
        Log.debug('isToday', {isToday});
        Log.debug('startStartRange', {startStartRange});
        Log.debug('startEndRange', {startEndRange});

        this.fromTimeOptions = this.generateRangeHours(startStartRange.format('HH:mm'), endStartRange.format('HH:mm'), interval);
        this.toTimeOptions = this.generateRangeHours(startEndRange.format('HH:mm'), endEndRange.format('HH:mm'), interval, this.maxHours, false);
        Log.debug('fromTimeOptions', {fromTimeOptions:this.fromTimeOptions});
        Log.debug('toTimeOptions', {toTimeOptions:this.toTimeOptions});

    }

    generateRangeHours(horaInicio: string, horaFin: string, intervalo: number = 15, limiteHoras?: number, fixedInterval: boolean = true): string[] {
        const [hInicio, mInicio] = horaInicio.split(':').map(Number);
        const [hFin, mFin] = horaFin.split(':').map(Number);

        const inicio = new Date();
        inicio.setHours(hInicio, mInicio, 0, 0);

        const fin = new Date();
        fin.setHours(hFin, mFin, 0, 0);

        const resultado: string[] = [];

        // Agregar la hora de inicio exacta
        resultado.push(inicio.toTimeString().slice(0, 5));

        // Siguiente múltiplo de intervalo alineado a :00, :15, :30, :45
        const siguiente = new Date(inicio);
        const minutos = siguiente.getMinutes();
        const offset = intervalo - (fixedInterval ? (minutos % intervalo) : 0);
        siguiente.setMinutes(minutos + offset);

        // Calcular el tope por limiteHoras (sin impedir incluir horaFin)
        const limite = limiteHoras
            ? new Date(inicio.getTime() + limiteHoras * 60 * 60 * 1000)
            : null;

        // Avanzar hasta llegar al límite (si hay) o a la hora de fin (lo que venga primero)
        while (!limite || siguiente <= limite) {
            // Detener si ya pasó la hora fin
            if (siguiente >= fin) break;
            resultado.push(siguiente.toTimeString().slice(0, 5));
            siguiente.setMinutes(siguiente.getMinutes() + intervalo);
        }

        // Asegurarse de incluir siempre la hora de fin
        const horaFinStr = fin.toTimeString().slice(0, 5);
        if (!resultado.includes(horaFinStr)) {
            resultado.push(horaFinStr);
        }

        return resultado;
    }

    toUpdateTimeFields() {
        if (this.fromTimeOptions.length > 0) {
            this.form.patchValue({ fromTime: this.fromTimeOptions[0] }, { emitEvent: false });
        }
        if (this.toTimeOptions.length > 0) {
            this.form.patchValue({ toTime: this.toTimeOptions[0] }, { emitEvent: false });
        }
    }

    getDuration() {

        if (this.toDate == undefined || this.fromDate == undefined) {
            return;
        }
        if (this.fromTime == undefined || this.toTime == undefined) {
            return;
        }

        let start = this.fromDate.clone();
        start.set('hours', this.fromTime.hour);
        start.set('minutes', this.fromTime.minute);

        let end = this.toDate.clone();
        end.set('hours', this.toTime.hour);
        end.set('minutes', this.toTime.minute);

        let duration = moment.duration(end.diff(start));
        let d = Math.floor(duration.asDays());
        let h = duration.hours();
        let m = duration.minutes();

        let days = this.appService.getTraduccion('days');
        let hours = this.appService.getTraduccion('hours');
        let minutes = this.appService.getTraduccion('minutes');
        let day = this.appService.getTraduccion('day');
        let hour = this.appService.getTraduccion('hour');
        let minute = this.appService.getTraduccion('minute');

        //console.log(start.toDate(), end.toDate());

        let daySrt = (d > 0) ? (d + ' ' + (d > 1 ? days : day)) + ',' : '';
        let hourSrt = (h > 0) ? (h + ' ' + (h > 1 ? hours : hour)) : '';
        let minuteSrt = (m > 0) ? (m + ' ' + (m > 1 ? minutes : minute)) : '';

        //9 días, 6 horas 55 minutos
        return daySrt + ' ' + hourSrt + ' ' + minuteSrt;
    }

    getFromDateTimeSrt() {
        const fromDate = this.form.value?.fromDate;
        if (fromDate == null || this.fromTime == null) {
            return "";
        }
        return this.formatDateTime(this.fromDate);
    }

    getToDateTimeSrt() {
        const toDate = this.form.value.toDate;
        if (toDate == null || this.toTime == null) {
            return "";
        }
        return this.formatDateTime(this.toDate);
    }

    plus() {
        this.toDate.add(1, 'days');
        this.updateCart();
    }

    less() {
        this.toDate.subtract(1, 'days');
        this.updateCart();
    }

    onDateSelection() {
        const fromDate: Date = this.form.get('fromDate')?.value;
        const toDate: Date = this.form.get('toDate')?.value;
        Log.debug('fromDate', {fromDate});
        Log.debug('toDate', {toDate});
        if (fromDate == null || toDate == null) {
            return;
        }

        const fromDateMoment: moment.Moment = this.mapDateAsMoment(fromDate);
        const toDateMoment: moment.Moment = this.mapDateAsMoment(toDate);
        Log.debug('fromDateMoment', {fromDateMoment});
        Log.debug('toDateMoment', {toDateMoment});

        this.fromDate = fromDateMoment;
        this.toDate = toDateMoment;

        this.configureTimeOptions();
        this.toUpdateTimeFields();
        this.updateCart();
    }

    onChangeFromTime(value) {
        Log.debug('onChangeFromTime', {value});
        //Workround para añadir una option que no exista cuando seteamos desde el padre
        if (value && !this.fromTimeOptions.includes(value)) {
            Log.warn(`Añadiendo valor dinámico: ${value}`);
            this.fromTimeOptions = [...this.fromTimeOptions, value];
        }else{
            const time = this.mapStringTimeToTimeStruct(value);
            this.fromDate.set({ "hour": time.hour, "minute": time.minute })
            this.configureTimeOptions();
            this.form.patchValue({ toTime: this.toTimeOptions[0] }, { emitEvent: false });
            this.onChangeToTime(this.toTimeOptions[0]);
        }
    }

    onChangeToTime(value) {
        Log.debug('onChangeToTime', value);
        //Workround para añadir una option que no exista cuando seteamos desde el padre
        if (value && !this.toTimeOptions.includes(value)) {
            Log.warn(`Añadiendo valor dinámico: ${value}`);
            this.toTimeOptions = [...this.toTimeOptions, value];
        }else{
            const time = this.mapStringTimeToTimeStruct(value);
            this.toDate.set({ "hour": time.hour, "minute": time.minute })
            this.updateCart();
        }
    }

    updateCart() {
        if (!this.form.valid) return;

        const fromDate = this.fromDate.clone().utc();
        const toDate = this.toDate.clone().utc();
        this.formUpdated.emit({ start: this.formatDateTime(fromDate), end: this.formatDateTime(toDate) });
    }



    // ========================================================
    // ====================== MAPEOS ==========================
    // ========================================================


    // ======================== DATE ==========================
    mapDateAsMoment(date: Date): moment.Moment {
        return moment.tz(
            {
                year: date.getFullYear(),
                month: date.getMonth(), // 0-11
                date: date.getDate(),   // día del mes
                hour: 0, minute: 0, second: 0, millisecond: 0
            },
            this.getCurrentTimezone()
        );
    }

    // ==================== TIMESTRUCT ========================
    mapStringTimeToTimeStruct(input: string): TimeStruct {
        let hours: number = Number(input.split(':')[0]);
        let minutes: number = Number(input.split(':')[1]);
        return {
            hour: hours,
            minute: minutes,
            second: 0
        }
    }

    mapMomentAsTimeStruct(datetime: moment.Moment): TimeStruct {
        if (datetime == undefined) {
            return null;
        }
        return {
            hour: datetime.hours(),
            minute: datetime.minutes(),
            second: 0
        }
    }

    formatDateTime(datetime: moment.Moment): string {
        if (datetime == undefined) {
            return "";
        }
        return datetime.format('DD-MM-YYYY HH:mm')
    }

    // ========================================================
    // ==================== UTILIDADES ========================
    // ========================================================


    getNow(): moment.Moment {
        const now = moment.tz(this.getCurrentTimezone());
        now.locale("es");
        //now.set('hour', 2); //TODO probar diferentes horas
        return now;
    }

    get minAsDate(): Date | null {
        return this.minDate?.toDate() ?? null;
    }

    get maxAsDate(): Date | null {
        return this.maxDate?.toDate() ?? null;
    }

    formatDate(date: Date | null | undefined): string {
        if (date == null) {
            return '';
        }

        return moment(date).format('DD-MM-YYYY');
    }

    private formatTimeStruct(time: TimeStruct | null): string {
        if (time == null) {
            return '';
        }

        return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
    }

    private getCurrentTimezone(): string {
        return this.tz || this.TIME_ZONE;
    }

    private getLocalTimeFromUtcHours(date: moment.Moment, time: string): moment.Moment {
        const [hour, minute] = time.split(':').map(value => Number(value.trim()));

        return moment.utc({
            year: date.year(),
            month: date.month(),
            date: date.date(),
            hour,
            minute,
            second: 0,
            millisecond: 0
        }).tz(this.getCurrentTimezone());
    }

    getDiffDays() {
        let dayFix = 0;
         //TODO DEPENDENCIA SIZE type=="BAGGAGE"
        if (this.parameters.type == null) {
            dayFix = 1;
        }
        if (this.toDate == undefined || this.fromDate == undefined) {
            return 0 + dayFix;
        }
        let a = this.toDate;
        let b = this.fromDate;
        return (a.diff(b, 'days')) + dayFix;
    }


}
